import { useState, useEffect, useMemo } from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "./ui/dialog";
import { Switch } from "./ui/switch";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter,
  AlertDialogTitle, AlertDialogDescription, AlertDialogAction, AlertDialogCancel,
} from "./ui/alert-dialog";
import { Package, Plus, Loader2, X, Check, Clock, Link2, TriangleAlert, ArrowRight, Pencil, Trash2, ChevronDown, Calendar, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { apiFetch } from "../utils/api";
import { formatCurrency } from "../utils/formatters";
import EditorTestoRicco from "./EditorTestoRicco";
import { DIALOG_CONTENT_CLS, InfoTooltip, PRIMARY_BTN, SECONDARY_BTN, SHADOWS } from "./designSystem";

// Modale di creazione/modifica di un servizio del catalogo
// (design_handoff_crea_servizio, variante 3a): navigazione laterale in due
// gruppi al posto dei sette tab orizzontali, che non entravano in larghezza.
// Corrispettivo e Modalità di pagamento si compongono dai dati economici:
// il backend li rigenera a ogni salvataggio (backend/proposta_economica.py),
// qui se ne mostra l'anteprima con gli importi evidenziati. Scritti a mano,
// possono citare gli importi con un segnaposto (chip «Importo» dell'editor),
// che resta collegato ai prezzi anche nei contratti.

// ==================== Sezioni ====================

// Testi della Proposta di Adesione, nell'ordine del PDF
// (backend/routes/contratti_pdf.py). `titolo` è quello stampato nel contratto.
const TESTI_LIBERI = [
  { id: "piano_progetto", label: "Piano di progetto", titolo: "Piano di progetto" },
  { id: "attivita_incluse", label: "Attività incluse", titolo: "Attività incluse - piano operativo mensile" },
  { id: "ripartizione_attivita", label: "Ripartizione delle attività", titolo: "Ripartizione delle attività" },
];
const TESTI_AUTO = [
  { id: "pagamento_corrispettivo", label: "Pagamento del corrispettivo", titolo: "Pagamento del corrispettivo", flag: "pagamento_corrispettivo_auto" },
  { id: "modalita_pagamento", label: "Modalità di pagamento", titolo: "Modalità di pagamento", flag: "modalita_pagamento_auto" },
];
const SEZ_SERVIZIO = "servizio";
const SEZ_TASK = "task_ruoli";
// Condizioni Generali di Contratto: precompilate col testo standard
// (backend/condizioni_generali.py), modificabili per il singolo servizio.
const SEZ_CONDIZIONI = "condizioni_generali";
const GRUPPI = [
  { titolo: "Configurazione", sezioni: [{ id: SEZ_SERVIZIO, label: "Servizio e prezzi" }, { id: SEZ_TASK, label: "Task e risorse" }] },
  { titolo: "Proposta di Adesione", sezioni: [...TESTI_LIBERI, ...TESTI_AUTO, { id: SEZ_CONDIZIONI, label: "Condizioni generali" }] },
];
// Numerazione continua 1–8 fra i due gruppi: è anche l'ordine di "Avanti".
const ORDINE = GRUPPI.flatMap((g) => g.sezioni);

// Task generate dalla Data Girato. Ogni servizio ha le sue, ciascuna con
// scadenza e ruoli propri: non si usano più «Offset Task» e «Risorse
// Assegnate» delle Impostazioni. Chiavi e nomi vengono dalla libreria
// condivisa (/task-libreria, backend/routes/catalogo_servizi.py), così una
// task creata qui si aggiunge anche agli altri servizi.
const SCAD_GIORNI = "giorni_girato";
// N° giorno della settimana del mese (es. 3° mercoledì): non dipende dal
// girato, scade alla prossima occorrenza da quando la task nasce. Il valore
// conserva il nome della prima versione ("del mese dopo il girato").
const SCAD_GIORNO_MESE = "giorno_mese_successivo";
// Giorni lavorativi dal pagamento dell'attivazione: la task nasce una volta
// sola, quando il pagamento viene registrato, e non segue il girato.
const SCAD_ATTIVAZIONE = "giorni_attivazione";
// Formato delle prime versioni, equivale al 1° mercoledì.
const SCAD_MERCOLEDI = "primo_mercoledi_mese_successivo";
const ULTIMA = -1;
const SETTIMANE = [
  { valore: 1, label: "1°" },
  { valore: 2, label: "2°" },
  { valore: 3, label: "3°" },
  { valore: 4, label: "4°" },
  { valore: ULTIMA, label: "ultimo" },
];
// Indici come datetime.weekday() del backend: 0 = lunedì.
const GIORNI_SETTIMANA = [
  { valore: 0, label: "Lun", nome: "lunedì" },
  { valore: 1, label: "Mar", nome: "martedì" },
  { valore: 2, label: "Mer", nome: "mercoledì" },
  { valore: 3, label: "Gio", nome: "giovedì" },
  { valore: 4, label: "Ven", nome: "venerdì" },
  { valore: 5, label: "Sab", nome: "sabato" },
  { valore: 6, label: "Dom", nome: "domenica" },
];
// Stesso limite del backend (TaskServizioInput).
const MAX_GIORNI = 60;

// `sigla`: tag in sola lettura della riga chiusa. Le chiavi sono i ruoli
// utente: "videomaker" è il Content Creator, "filmmaker" il Videomaker.
const RUOLI_TASK = [
  { id: "smm", label: "SMM", sigla: "SMM" },
  { id: "videomaker", label: "Content Creator", sigla: "CC" },
  { id: "filmmaker", label: "Videomaker", sigla: "Videomaker" },
  { id: "editor", label: "Editor", sigla: "Editor" },
  { id: "pm", label: "PM", sigla: "PM" },
];

// Nel form i giorni sono un numero senza segno più il verso (prima/dopo il
// girato): più chiaro da compilare di un negativo da digitare.
function voceForm(t) {
  const g = Number(t.giorni) || 0;
  const mercoledi = t.tipo_scadenza === SCAD_MERCOLEDI;
  return {
    task_key: t.task_key,
    tipo_scadenza: mercoledi ? SCAD_GIORNO_MESE : t.tipo_scadenza || SCAD_GIORNI,
    giorni: String(Math.abs(g)),
    verso: g < 0 ? "prima" : "dopo",
    settimana: mercoledi ? 1 : t.settimana ?? 1,
    giorno_settimana: mercoledi ? 2 : t.giorno_settimana ?? 2,
    ruoli: [...(t.ruoli || [])],
  };
}

const contaGiorni = (v) => v.tipo_scadenza === SCAD_GIORNI || v.tipo_scadenza === SCAD_ATTIVAZIONE;

function giorniValidi(v) {
  return !contaGiorni(v) || (/^\d{1,2}$/.test(v.giorni.trim()) && Number(v.giorni) <= MAX_GIORNI);
}

function descriviScadenza(v) {
  if (v.tipo_scadenza === SCAD_GIORNO_MESE) {
    const giorno = GIORNI_SETTIMANA.find((g) => g.valore === v.giorno_settimana)?.nome || "";
    // "domenica" è l'unico femminile: 2ª domenica, ultima domenica.
    const femminile = v.giorno_settimana === 6;
    const ordinale = v.settimana === ULTIMA ? (femminile ? "Ultima" : "Ultimo") : `${v.settimana}${femminile ? "ª" : "°"}`;
    return `${ordinale} ${giorno} del mese`;
  }
  if (!giorniValidi(v)) return `Indica da 0 a ${MAX_GIORNI} giorni lavorativi`;
  const n = Number(v.giorni);
  if (v.tipo_scadenza === SCAD_ATTIVAZIONE) {
    if (n === 0) return "Stesso giorno del pagamento dell'attivazione";
    return `${n === 1 ? "1 giorno lavorativo" : `${n} giorni lavorativi`} dopo il pagamento dell'attivazione`;
  }
  if (n === 0) return "Stesso giorno del girato";
  const giorni = n === 1 ? "1 giorno lavorativo" : `${n} giorni lavorativi`;
  return v.verso === "prima" ? `${giorni} prima del girato` : `${giorni} dopo il girato`;
}

// ---- Esempio di scadenza ----
// Stesse regole del backend (server.py: add_business_days e
// prossimo_giorno_del_mese): lavorativi = lunedì–venerdì, nessuna festività.
// Se il backend inizierà a saltare le festività, l'esempio va allineato.

const giornoIso = (d) => (d.getDay() + 6) % 7; // 0 = lunedì, come weekday()

function aggiungiGiorniLavorativi(inizio, giorni) {
  const d = new Date(inizio);
  if (giornoIso(d) >= 5) d.setDate(d.getDate() + (giorni >= 0 ? 7 - giornoIso(d) : -(giornoIso(d) - 4)));
  const passo = giorni >= 0 ? 1 : -1;
  for (let restanti = Math.abs(giorni); restanti > 0;) {
    d.setDate(d.getDate() + passo);
    if (giornoIso(d) < 5) restanti -= 1;
  }
  return d;
}

// N° giorno della settimana del mese che parte da `mese` (0 = gennaio).
function giornoDelMese(anno, mese, settimana, giornoSettimana) {
  if (settimana === ULTIMA) {
    const ultimo = new Date(anno, mese + 1, 0);
    ultimo.setDate(ultimo.getDate() - ((giornoIso(ultimo) - giornoSettimana + 7) % 7));
    return ultimo;
  }
  const primo = new Date(anno, mese, 1);
  primo.setDate(primo.getDate() + ((giornoSettimana - giornoIso(primo) + 7) % 7) + 7 * (settimana - 1));
  return primo;
}

// Quello di questo mese se non è ancora passato, altrimenti quello del mese dopo.
function prossimoGiornoDelMese(da, settimana, giornoSettimana) {
  const questo = giornoDelMese(da.getFullYear(), da.getMonth(), settimana, giornoSettimana);
  return questo >= da ? questo : giornoDelMese(da.getFullYear(), da.getMonth() + 1, settimana, giornoSettimana);
}

const MESI_BREVI = ["gen", "feb", "mar", "apr", "mag", "giu", "lug", "ago", "set", "ott", "nov", "dic"];
// "l'8 set", "il 15 ott": l'articolo si elide davanti a 1, 8 e 11.
const conArticolo = (d) => `${[1, 8, 11].includes(d.getDate()) ? "l'" : "il "}${d.getDate()} ${MESI_BREVI[d.getMonth()]}`;

// Il servizio non ha una Data Girato sua: l'esempio parte da oggi.
function esempioScadenza(v) {
  if (!giorniValidi(v)) return null;
  const girato = new Date();
  girato.setHours(0, 0, 0, 0);
  if (v.tipo_scadenza === SCAD_ATTIVAZIONE) {
    return `Esempio: attivazione pagata ${conArticolo(girato)}, scade ${conArticolo(aggiungiGiorniLavorativi(girato, Number(v.giorni)))}`;
  }
  if (v.tipo_scadenza === SCAD_GIORNO_MESE) {
    return `Esempio: task creata ${conArticolo(girato)}, scade ${conArticolo(prossimoGiornoDelMese(girato, v.settimana, v.giorno_settimana))}`;
  }
  const scade = aggiungiGiorniLavorativi(girato, Number(v.giorni) * (v.verso === "prima" ? -1 : 1));
  return `Esempio: girato ${conArticolo(girato)}, scade ${conArticolo(scade)}`;
}

// Un servizio nuovo parte dalle task storiche, coi tempi e ruoli di partenza
// registrati in libreria.
function taskIniziali(libreria) {
  return libreria.filter((t) => t.storica).map(voceForm);
}

// ==================== Numeri ====================

// Accetta sia "1.234,56" sia "1234.56": con la virgola i punti sono migliaia;
// senza, un punto seguito da gruppi di tre cifre è migliaia ("1.000"), altrimenti
// è il separatore decimale. NaN se il testo non è un numero.
function leggiNumero(testo) {
  const t = String(testo ?? "").trim().replace(/\s/g, "");
  if (t === "") return NaN;
  let normalizzato;
  if (t.includes(",")) normalizzato = t.replace(/\./g, "").replace(",", ".");
  else if (/^\d{1,3}(\.\d{3})+$/.test(t)) normalizzato = t.replace(/\./g, "");
  else normalizzato = t;
  return /^\d+(\.\d+)?$/.test(normalizzato) ? Number(normalizzato) : NaN;
}

// Valore numerico → testo del campo, nello stesso formato che leggiNumero accetta.
function scriviNumero(valore) {
  if (valore === null || valore === undefined || valore === "") return "";
  return raggruppa(Number(valore), false);
}

// Formatter esplicito: Intl it-IT non raggruppa le cifre sotto 10.000
// (minimumGroupingDigits), mentre nel contratto serve sempre "1.000".
function raggruppa(n, dueDecimali) {
  const fisso = dueDecimali ? n.toFixed(2) : String(Math.round(n * 100) / 100);
  const [intero, decimali] = fisso.split(".");
  const conPunti = intero.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  return decimali ? `${conPunti},${decimali}` : conPunti;
}

const eur2 = (n) => `€ ${raggruppa(n || 0, true)}`;
const pct = (n) => `${raggruppa(n || 0, false)}%`;
const numeroOZero = (testo) => {
  const n = leggiNumero(testo);
  return Number.isFinite(n) ? n : 0;
};

// ==================== Testi generati ====================

// Stessi template di backend/proposta_economica.py, che è la fonte che
// finisce nel contratto: qui servono per l'anteprima. Ogni riga è
// { tipo, pre, tok, post }, con `tok` la parte che viene dai dati economici.
const GIORNO_SCADENZA = 3;
const METODO_PAGAMENTO = "bonifico bancario";

// Attivazioni successive alla prima (servizi eventi) prima degli altri addon:
// nei testi e nel contratto si leggono di seguito all'attivazione principale.
function addonOrdinati(addons) {
  return [...addons.filter((a) => a.attivazione), ...addons.filter((a) => !a.attivazione)];
}

function bloccoCorrispettivo(form) {
  const nome = form.nome.trim() || "servizio";
  const voceAttivazione = form.nome_attivazione.trim().toLowerCase() || "attivazione";
  const righe = [
    { tipo: "p", pre: "Il corrispettivo complessivo per l'espletamento dei servizi sopra indicati è pari a:" },
    { tipo: "li", pre: `(una tantum) ${voceAttivazione} del ${nome} `, tok: eur2(numeroOZero(form.importo_attivazione)), chiave: SEGNAPOSTO_ATTIVAZIONE, post: " + IVA" },
  ];
  for (const a of addonOrdinati(form.addons)) {
    const label = a.label.trim().toLowerCase();
    const valore = numeroOZero(a.valore);
    if (a.tipo_valore === "percentuale") {
      righe.push({ tipo: "li", pre: `una ${label} pari al `, tok: pct(valore), chiave: a.addon_id, post: ` sul fatturato lordo generato dalle vendite online tramite ${nome} nel periodo di riferimento.` });
    } else if (a.ricorrenza === "mensile") {
      righe.push({ tipo: "li", pre: `una ${label} di `, tok: eur2(valore), chiave: a.addon_id, post: " + IVA;" });
    } else {
      righe.push({ tipo: "li", pre: `(una tantum) ${label} `, tok: eur2(valore), chiave: a.addon_id, post: " + IVA" });
    }
  }
  return [...righe, ...righeRiepilogo(form)];
}

// Primo mese e mesi successivi (_riepilogo nel backend): solo se i due importi
// possono differire. Nel catalogo l'attivazione c'è sempre.
function righeRiepilogo(form) {
  const fissi = form.addons.filter((a) => a.tipo_valore !== "percentuale");
  const unaTantum = numeroOZero(form.importo_attivazione)
    + fissi.filter((a) => a.ricorrenza !== "mensile").reduce((t, a) => t + numeroOZero(a.valore), 0);
  const mensile = fissi.filter((a) => a.ricorrenza === "mensile").reduce((t, a) => t + numeroOZero(a.valore), 0);
  if (!unaTantum || !mensile) return [];
  const dalSecondo = form.primo_mese_solo_attivazione;
  const variabile = form.addons.some((a) => a.tipo_valore === "percentuale") ? " oltre alla quota variabile" : "";
  return [
    { tipo: "p", pre: "Riepilogo:" },
    { tipo: "li", pre: "Primo mese: ", tok: eur2(dalSecondo ? unaTantum : unaTantum + mensile), post: ` + IVA${dalSecondo ? "" : variabile}` },
    { tipo: "li", pre: "Dal secondo mese: ", tok: eur2(mensile), post: ` + IVA al mese${variabile}` },
  ];
}

function bloccoModalita(form) {
  const mensile = `corrisposta mediante ${METODO_PAGAMENTO}, da effettuarsi entro il giorno ${GIORNO_SCADENZA} di ogni mese.`;
  // La fee mensile decorre dal pagamento dell'attivazione, la quota variabile
  // resta sulla scadenza fissa del mese. Nel catalogo l'attivazione c'è
  // sempre, quindi la frase non dipende da primo_mese_solo_attivazione.
  const feeMensile = `corrisposta mediante ${METODO_PAGAMENTO} da effettuare entro 30gg dal pagamento dell’attivazione.`;
  const nomeAtt = form.nome_attivazione.trim();
  const righe = [
    nomeAtt
      ? { tipo: "li", pre: `${nomeAtt.charAt(0).toUpperCase() + nomeAtt.slice(1)} (`, tok: eur2(numeroOZero(form.importo_attivazione)), chiave: SEGNAPOSTO_ATTIVAZIONE, post: ` + IVA) corrisposta mediante ${METODO_PAGAMENTO}, il servizio verrà avviato al pagamento di questa quota.` }
      : { tipo: "li", pre: "Quota una tantum (", tok: eur2(numeroOZero(form.importo_attivazione)), chiave: SEGNAPOSTO_ATTIVAZIONE, post: ` + IVA) corrisposta mediante ${METODO_PAGAMENTO}, il servizio verrà avviato al pagamento dell’attivazione.` },
  ];
  for (const a of addonOrdinati(form.addons)) {
    const grezza = a.label.trim();
    const label = grezza.charAt(0).toUpperCase() + grezza.slice(1);
    const valore = numeroOZero(a.valore);
    if (a.tipo_valore === "percentuale") {
      righe.push({ tipo: "li", pre: "Quota variabile (", tok: pct(valore), chiave: a.addon_id, post: ` sul fatturato lordo): rendicontata mensilmente e ${mensile}` });
    } else if (a.ricorrenza === "mensile") {
      righe.push({ tipo: "li", pre: `${label} (`, tok: eur2(valore), chiave: a.addon_id, post: ` + IVA) ${feeMensile}` });
    } else {
      righe.push({ tipo: "li", pre: `${label} (`, tok: eur2(valore), chiave: a.addon_id, post: ` + IVA): corrisposta mediante ${METODO_PAGAMENTO}.` });
    }
  }
  return righe;
}

const GENERATORI = { pagamento_corrispettivo: bloccoCorrispettivo, modalita_pagamento: bloccoModalita };

function escapeHtml(testo) {
  return testo.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

// Righe → HTML dell'editor: i token diventano testo semplice, o con
// `collegati` il segnaposto del loro importo (testo scritto a mano che parte
// da quello generato e resta agganciato ai prezzi).
function righeAHtml(righe, collegati = false) {
  let html = "";
  let inElenco = false;
  for (const r of righe) {
    const tok = r.tok ? (collegati && r.chiave ? `{{${r.chiave}}}` : r.tok) : "";
    const testo = escapeHtml(`${r.pre}${tok}${r.post || ""}`);
    if (r.tipo === "li" && !inElenco) { html += "<ul>"; inElenco = true; }
    if (r.tipo !== "li" && inElenco) { html += "</ul>"; inElenco = false; }
    html += r.tipo === "li" ? `<li><p>${testo}</p></li>` : `<p>${testo}</p>`;
  }
  return inElenco ? `${html}</ul>` : html;
}

function testoVisibile(html) {
  if (!html) return "";
  const doc = new DOMParser().parseFromString(html, "text/html");
  return (doc.body.textContent || "").replace(/\s+/g, " ").trim();
}

// Testo per il confronto con quello generato: blocco per blocco, spazi
// compattati e senza la punteggiatura finale, che a mano varia facilmente.
function firmaTesto(html) {
  const doc = new DOMParser().parseFromString(html || "", "text/html");
  return [...doc.body.querySelectorAll("p, h3")]
    .map((el) => el.textContent.replace(/\s+/g, " ").trim().replace(/[.;:,]+$/, ""))
    .filter(Boolean)
    .join("|");
}

// Chiavi dei segnaposto citati in un testo scritto a mano.
function chiaviSegnaposti(html) {
  return [...(html || "").matchAll(/\{\{([A-Za-z0-9_-]+)\}\}/g)].map((m) => m[1]);
}

// ==================== Form ====================

// Un servizio nuovo parte con la frase d'apertura del modello di contratto:
// resta da scrivere solo l'elenco.
const PIANO_INIZIALE = "<p>Il progetto comprende:</p><ul><li><p></p></li></ul>";

let prossimaChiave = 1;
const chiaveAddon = () => `a${prossimaChiave++}`;
// Stesso formato del backend (catalogo_servizi._nuovo_addon_id). Lo sceglie
// il client perché un testo a mano può citare l'importo di un addon appena
// aggiunto, prima del salvataggio.
const nuovoAddonId = () =>
  `addon_${[...crypto.getRandomValues(new Uint8Array(4))].map((b) => b.toString(16).padStart(2, "0")).join("")}`;
// Segnaposto dell'importo di attivazione (VOCE_ATTIVAZIONE nel backend).
const SEGNAPOSTO_ATTIVAZIONE = "attivazione";

function formDaServizio(servizio, libreria) {
  if (!servizio) {
    return {
      nome: "",
      descrizione: "",
      importo_attivazione: "",
      nome_attivazione: "",
      primo_mese_solo_attivazione: true,
      addons: [],
      piano_progetto: PIANO_INIZIALE,
      attivita_incluse: "",
      ripartizione_attivita: "",
      pagamento_corrispettivo: "",
      modalita_pagamento: "",
      pagamento_corrispettivo_auto: true,
      modalita_pagamento_auto: true,
      condizioni_generali: "",
      task: taskIniziali(libreria),
    };
  }
  const form = {
    nome: servizio.nome,
    descrizione: servizio.descrizione || "",
    importo_attivazione: scriviNumero(servizio.importo_attivazione),
    nome_attivazione: servizio.nome_attivazione || "",
    primo_mese_solo_attivazione: servizio.primo_mese_solo_attivazione ?? true,
    addons: (servizio.addons || []).map((a) => ({
      chiave: chiaveAddon(),
      addon_id: a.addon_id || nuovoAddonId(),
      label: a.label,
      ricorrenza: a.ricorrenza || "una_tantum",
      tipo_valore: a.tipo_valore || "importo",
      valore: scriviNumero(a.valore),
      attivazione: !!a.attivazione,
    })),
    ...Object.fromEntries(TESTI_LIBERI.map(({ id }) => [id, servizio[id] || ""])),
    // Scritti a mano con importi collegati: si modifica il testo coi
    // segnaposto, non quello risolto coi prezzi.
    ...Object.fromEntries(TESTI_AUTO.map(({ id }) => [id, servizio[`${id}_modello`] || servizio[id] || ""])),
    // Vuote = il servizio usa il testo standard: il campo si precompila con
    // quello appena arriva dal backend.
    condizioni_generali: servizio.condizioni_generali || "",
    // Il backend dà la lista a ogni servizio all'avvio: il ripiego serve solo
    // se la modale si apre prima che la migrazione sia girata.
    task: servizio.task ? servizio.task.map(voceForm) : taskIniziali(libreria),
  };
  // Servizi salvati prima della modalità automatica: diventano automatici
  // solo se il testo coincide con quello che si genererebbe, altrimenti
  // restano manuali e non si perde nulla di scritto a mano.
  for (const { id, flag } of TESTI_AUTO) {
    form[flag] = typeof servizio[flag] === "boolean"
      ? servizio[flag]
      : firmaTesto(servizio[id]) === firmaTesto(righeAHtml(GENERATORI[id](form)));
  }
  return form;
}

const plurale = (n, uno, molti) => `${n} ${n === 1 ? uno : molti}`;

function contaVoci(html) {
  // La frase d'apertura precompilata da sola non è ancora un testo.
  if (testoVisibile(html) === "" || testoVisibile(html) === testoVisibile(PIANO_INIZIALE)) return "Da scrivere";
  const doc = new DOMParser().parseFromString(html, "text/html");
  const titoletti = doc.querySelectorAll("h3").length;
  if (titoletti) return plurale(titoletti, "blocco", "blocchi");
  const voci = [...doc.querySelectorAll("li")].filter((li) => li.textContent.trim()).length;
  if (voci) return plurale(voci, "voce", "voci");
  // Testi incollati come semplici paragrafi, senza titoletti né elenchi.
  const paragrafi = [...doc.querySelectorAll("p")].filter((p) => p.textContent.trim()).length;
  return plurale(paragrafi, "paragrafo", "paragrafi");
}

// Errori per sezione: alimentano il pallino rosso nella navigazione e il
// blocco del salvataggio. Stesse regole del backend (ServizioInput.testi_pdf).
function erroriForm(form) {
  const campi = {};
  if (!form.nome.trim()) campi.nome = true;
  if (!form.descrizione.trim()) campi.descrizione = true;
  if (form.importo_attivazione.trim() !== "" && !Number.isFinite(leggiNumero(form.importo_attivazione))) campi.importo = true;
  form.addons.forEach((a) => {
    if (!a.label.trim()) campi[`label-${a.chiave}`] = true;
    const n = leggiNumero(a.valore);
    if (a.valore.trim() !== "" && !Number.isFinite(n)) campi[`valore-${a.chiave}`] = true;
    if (a.tipo_valore === "percentuale" && n > 100) campi[`valore-${a.chiave}`] = true;
  });
  const sezioni = new Set();
  if (Object.keys(campi).length) sezioni.add(SEZ_SERVIZIO);
  form.task.forEach((v) => {
    if (!giorniValidi(v)) {
      campi[`giorni-${v.task_key}`] = true;
      sezioni.add(SEZ_TASK);
    }
  });
  for (const { id } of TESTI_LIBERI) {
    const testo = testoVisibile(form[id]);
    if (!testo || (id === "piano_progetto" && testo === testoVisibile(PIANO_INIZIALE))) sezioni.add(id);
  }
  const voci = new Set([SEGNAPOSTO_ATTIVAZIONE, ...form.addons.map((a) => a.addon_id)]);
  for (const { id, flag } of TESTI_AUTO) {
    if (form[flag]) continue;
    if (!testoVisibile(form[id]) && !chiaviSegnaposti(form[id]).length) sezioni.add(id);
    // Importo di un addon eliminato: il backend rifiuterebbe il salvataggio.
    if (chiaviSegnaposti(form[id]).some((c) => !voci.has(c))) {
      campi[`segnaposti-${id}`] = true;
      sezioni.add(id);
    }
  }
  return { campi, sezioni };
}

// ==================== Stili ====================

const FOCUS = "focus:border-[#5b5bd6] focus:shadow-[0_0_0_3px_rgba(91,91,214,.14)]";
const FOCUS_WITHIN = "focus-within:border-[#5b5bd6] focus-within:shadow-[0_0_0_3px_rgba(91,91,214,.14)]";
const BORDO_ERRORE = "!border-[#c0392b]";
const COLONNE_ECONOMICI = "sm:grid-cols-[minmax(0,1fr)_190px_176px_36px]";
const ETICHETTA_CAMPO = "text-[12.5px] font-semibold text-[#565d6d]";
// Task e risorse (design_handoff_task_risorse, 11a).
const ETICHETTA_BLOCCO = "text-[11px] font-semibold uppercase tracking-[.06em] text-[#9095a1]";
// Riga aperta che si stacca dalla lista: livello 3 attenuato del design handoff.
const OMBRA_RIGA_APERTA = "shadow-[0_2px_6px_rgba(15,23,42,.08),0_18px_36px_-16px_rgba(15,23,42,.30)]";

// Select della frase di scadenza: nativo, con la freccia disegnata sopra.
// x-excluded: in sviluppo il plugin visual-edits (craco.config.js) avvolge in
// uno <span> i figli dinamici dei tag HTML, e dentro <select>/<option> le
// opzioni sparirebbero dal menu. È l'esclusione prevista dal plugin; in
// produzione resta un attributo inerte.
function SelectFrase({ value, onChange, opzioni, testId, label }) {
  return (
    <span className="relative inline-flex">
      <select
        x-excluded="true"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        aria-label={label}
        className={`h-[34px] appearance-none rounded-[9px] border border-[#e4e4ed] bg-white pl-3 pr-[30px] text-[14px] font-semibold text-[#15161c] cursor-pointer outline-none transition-[border-color,box-shadow] ${FOCUS}`}
        data-testid={testId}
      >
        {opzioni.map((o) => <option key={o.valore} value={o.valore} x-excluded="true">{o.label}</option>)}
      </select>
      <ChevronDown className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 w-3 h-3 text-[#9095a1]" strokeWidth={2.4} />
    </span>
  );
}

function Segmentato({ opzioni, valore, onChange, testIdPrefix, piccolo = false }) {
  return (
    <div className={`flex bg-[#f1f1f7] ${piccolo ? "p-0.5 rounded-[7px]" : "p-[3px] rounded-[10px] gap-0.5"}`}>
      {opzioni.map((o) => {
        const attivo = valore === o.valore;
        return (
          <button
            key={o.valore}
            type="button"
            onClick={() => onChange(o.valore)}
            aria-pressed={attivo}
            className={`${piccolo ? "w-[26px] h-[26px] rounded-[6px] scheda-font-mono" : "flex-1 h-[30px] rounded-[8px]"} text-[12.5px] font-semibold transition-colors ${
              attivo ? "bg-white text-[#15161c] shadow-[0_1px_2px_rgba(20,20,50,.12)]" : "text-[#6b7280] hover:text-[#15161c]"
            }`}
            data-testid={`${testIdPrefix}-${o.valore}`}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

function IntestazioneSezione({ titolo, children }) {
  return (
    <div className="flex flex-col gap-1.5">
      <h3 className="text-[18px] font-bold leading-[1.2] text-[#15161c]">{titolo}</h3>
      <p className="text-[13px] font-medium leading-[1.5] text-[#6b7280] [text-wrap:pretty]">{children}</p>
    </div>
  );
}

// Anteprima di un testo generato: stessa tipografia dell'editor, senza
// toolbar, con gli importi derivati dai dati evidenziati come token.
function AnteprimaGenerata({ righe, testId }) {
  return (
    <div className="rounded-[14px] border border-[#e4e4ed] px-[22px] pt-[18px] pb-[22px] flex flex-col gap-1.5" data-testid={testId}>
      {righe.map((r, i) =>
        r.tipo === "li" ? (
          <div key={i} className="flex gap-[11px] text-[14.5px] leading-[1.65] text-[#3c414d]">
            <span className="w-[5px] h-[5px] rounded-full bg-[#9095a1] mt-[10px] shrink-0" />
            <span className="[text-wrap:pretty]">
              {r.pre}
              {r.tok && (
                <span className="inline-flex items-center h-[22px] px-[7px] mx-px rounded-[6px] bg-[#eeeefc] text-[#3f3fb0] text-[13px] font-semibold scheda-font-mono">
                  {r.tok}
                </span>
              )}
              {r.post}
            </span>
          </div>
        ) : (
          <div key={i} className="text-[14.5px] leading-[1.65] text-[#3c414d]">{r.pre}</div>
        )
      )}
    </div>
  );
}

// ==================== Componente ====================

// `servizio`: null per un nuovo servizio. Il montaggio è condizionato dal
// chiamante (CatalogoServiziPanel) al ruolo admin/direttore commerciale.
// `ambito` vale solo per un servizio nuovo: quello di un servizio esistente
// non cambia (backend/routes/catalogo_servizi.py).
export default function ServizioDialog({ open, servizio, ambito = "standard", onClose, onSaved }) {
  // Più attivazioni con nome: solo nel catalogo eventi.
  const eventi = (servizio ? servizio.ambito : ambito) === "eventi";
  const [libreria, setLibreria] = useState([]);
  // Testo standard delle Condizioni Generali, per precompilare e ripristinare.
  const [standard, setStandard] = useState("");
  const [form, setForm] = useState(() => formDaServizio(null, []));
  const [iniziale, setIniziale] = useState("");
  const [sezione, setSezione] = useState(SEZ_SERVIZIO);
  const [salvando, setSalvando] = useState(false);
  const [tentato, setTentato] = useState(false);
  // Conferma generica (chiusura con modifiche, ritorno all'automatico,
  // eliminazione di una task dalla libreria).
  const [conferma, setConferma] = useState(null);
  const [nuovaTask, setNuovaTask] = useState("");
  const [creandoTask, setCreandoTask] = useState(false);
  // { task_key, nome } della task che si sta rinominando.
  const [rinomina, setRinomina] = useState(null);
  // Task e risorse: si modifica una task alla volta, aprendone la riga.
  const [apertaKey, setApertaKey] = useState(null);
  const [aggiungiAperto, setAggiungiAperto] = useState(false);

  // Il form si ricostruisce a ogni apertura, non a ogni arrivo della libreria:
  // arrivando dopo, sovrascriverebbe quello che l'utente ha già toccato.
  useEffect(() => {
    if (!open) return;
    const nuovo = formDaServizio(servizio, libreria);
    setForm(nuovo);
    setIniziale(JSON.stringify(nuovo));
    setSezione(SEZ_SERVIZIO);
    setTentato(false);
    setNuovaTask("");
    setRinomina(null);
    setApertaKey(null);
    setAggiungiAperto(false);
    let annullato = false;
    (async () => {
      try {
        const res = await apiFetch("/task-libreria");
        if (!res.ok || annullato) return;
        const items = (await res.json()).items || [];
        setLibreria(items);
        // Alla prima apertura la libreria non c'era ancora: un servizio nuovo
        // riceve ora le task di partenza, se nel frattempo nessuno le ha toccate.
        if (!servizio) {
          const conTask = (f) => (f.task.length ? f : { ...f, task: taskIniziali(items) });
          setForm(conTask);
          setIniziale((prec) => (prec ? JSON.stringify(conTask(JSON.parse(prec))) : prec));
        }
      } catch {
        // Senza libreria restano i nomi tecnici e non si aggiungono task.
      }
    })();
    (async () => {
      try {
        const res = await apiFetch("/catalogo-servizi/condizioni-standard");
        if (!res.ok || annullato) return;
        const { html } = await res.json();
        setStandard(html || "");
        // Stesso accorgimento delle task: il campo vuoto riceve il testo
        // standard senza che la modale risulti modificata.
        const conCondizioni = (f) => (f.condizioni_generali ? f : { ...f, condizioni_generali: html || "" });
        setForm(conCondizioni);
        setIniziale((prec) => (prec ? JSON.stringify(conCondizioni(JSON.parse(prec))) : prec));
      } catch {
        // Senza testo standard il campo resta vuoto: il PDF usa comunque lo standard.
      }
    })();
    return () => { annullato = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, servizio]);

  const errori = useMemo(() => erroriForm(form), [form]);
  const generati = useMemo(
    () => Object.fromEntries(TESTI_AUTO.map(({ id }) => [id, GENERATORI[id](form)])),
    [form]
  );
  const modificato = iniziale !== "" && JSON.stringify(form) !== iniziale;
  const aggiorna = (patch) => setForm((p) => ({ ...p, ...patch }));

  // ---- Dati economici ----

  const aggiungiAddon = () => {
    aggiorna({ addons: [...form.addons, { chiave: chiaveAddon(), addon_id: nuovoAddonId(), label: "", ricorrenza: "mensile", tipo_valore: "importo", valore: "" }] });
  };
  const aggiornaAddon = (chiave, patch) => {
    setForm((p) => ({ ...p, addons: p.addons.map((a) => (a.chiave === chiave ? { ...a, ...patch } : a)) }));
  };
  // Servizi eventi: attivazioni oltre alla prima, una tantum e a importo fisso.
  const aggiungiAttivazione = () => {
    aggiorna({ addons: [...form.addons, { chiave: chiaveAddon(), addon_id: nuovoAddonId(), label: "", ricorrenza: "una_tantum", tipo_valore: "importo", valore: "", attivazione: true }] });
  };
  const attivazioniExtra = form.addons.filter((a) => a.attivazione);
  const addonNormali = form.addons.filter((a) => !a.attivazione);
  const rimuoviAddon = (chiave) => {
    setForm((p) => ({ ...p, addons: p.addons.filter((a) => a.chiave !== chiave) }));
  };

  const attivazione = numeroOZero(form.importo_attivazione);
  const allaFirma = attivazione + form.addons
    .filter((a) => a.ricorrenza === "una_tantum" && a.tipo_valore === "importo")
    .reduce((t, a) => t + numeroOZero(a.valore), 0);
  const mensili = form.addons.filter((a) => a.ricorrenza === "mensile");
  const mensileFisso = mensili.filter((a) => a.tipo_valore === "importo").reduce((t, a) => t + numeroOZero(a.valore), 0);
  const mensilePct = mensili.filter((a) => a.tipo_valore === "percentuale").map((a) => pct(numeroOZero(a.valore)));
  const ogniMese = [mensileFisso ? formatCurrency(mensileFisso) : null, ...mensilePct].filter(Boolean).join(" + ");

  // ---- Task ----

  const perChiave = useMemo(() => Object.fromEntries(libreria.map((t) => [t.task_key, t])), [libreria]);
  const nomeTask = (chiave) => perChiave[chiave]?.nome || chiave;
  const disponibili = libreria.filter((t) => !form.task.some((v) => v.task_key === t.task_key));

  const aggiornaTask = (chiave, patch) => {
    setForm((p) => ({ ...p, task: p.task.map((v) => (v.task_key === chiave ? { ...v, ...patch } : v)) }));
  };
  const alternaRuoloTask = (chiave, ruolo) => {
    setForm((p) => ({
      ...p,
      task: p.task.map((v) => {
        if (v.task_key !== chiave) return v;
        return { ...v, ruoli: v.ruoli.includes(ruolo) ? v.ruoli.filter((r) => r !== ruolo) : [...v.ruoli, ruolo] };
      }),
    }));
  };
  const rimuoviTask = (chiave) => {
    setForm((p) => ({ ...p, task: p.task.filter((v) => v.task_key !== chiave) }));
    setApertaKey((k) => (k === chiave ? null : k));
  };
  // La task aggiunta si apre subito: tempi e ruoli sono da decidere.
  const aggiungiTask = (t) => {
    setForm((p) => ({ ...p, task: [...p.task, voceForm(t)] }));
    setApertaKey(t.task_key);
    setAggiungiAperto(false);
  };

  // Creazione e rinomina scrivono subito in libreria, fuori dal salvataggio
  // del servizio: la libreria è condivisa, non è un dato di questo servizio.
  const creaTask = async () => {
    const nome = nuovaTask.trim();
    if (!nome || creandoTask) return;
    setCreandoTask(true);
    try {
      const res = await apiFetch("/task-libreria", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ nome, tipo_scadenza: SCAD_GIORNI, giorni: 0, ruoli: [] }),
      });
      const dati = await res.json().catch(() => ({}));
      if (!res.ok) {
        toast.error(typeof dati.detail === "string" ? dati.detail : "Errore nella creazione della task");
        return;
      }
      setLibreria((l) => [...l, dati]);
      aggiungiTask(dati);
      setNuovaTask("");
    } catch {
      toast.error("Errore nella creazione della task");
    } finally {
      setCreandoTask(false);
    }
  };

  const confermaRinomina = async () => {
    if (!rinomina) return;
    const nome = rinomina.nome.trim();
    if (!nome || nome === nomeTask(rinomina.task_key)) {
      setRinomina(null);
      return;
    }
    try {
      const res = await apiFetch(`/task-libreria/${rinomina.task_key}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ nome }),
      });
      const dati = await res.json().catch(() => ({}));
      if (!res.ok) {
        toast.error(typeof dati.detail === "string" ? dati.detail : "Errore nella rinomina della task");
        return;
      }
      setLibreria((l) => l.map((t) => (t.task_key === dati.task_key ? dati : t)));
      setRinomina(null);
      toast.success("Task rinominata in tutti i servizi");
    } catch {
      toast.error("Errore nella rinomina della task");
    }
  };

  const eliminaDaLibreria = (t) => {
    setConferma({
      titolo: "Eliminare la task dalla libreria?",
      descrizione: `«${t.nome}» non sarà più disponibile per nessun servizio. Le task già generate per i clienti restano.`,
      etichetta: "Elimina",
      azione: async () => {
        try {
          const res = await apiFetch(`/task-libreria/${t.task_key}`, { method: "DELETE" });
          if (res.ok) {
            setLibreria((l) => l.filter((x) => x.task_key !== t.task_key));
            toast.success("Task eliminata dalla libreria");
          } else {
            const err = await res.json().catch(() => ({}));
            toast.error(typeof err.detail === "string" ? err.detail : "Errore nell'eliminazione della task");
          }
        } catch {
          toast.error("Errore nell'eliminazione della task");
        }
      },
    });
  };

  // ---- Testi automatici ----

  const scriviAMano = ({ id, flag }) => {
    aggiorna({ [flag]: false, [id]: righeAHtml(generati[id], true) });
  };
  const tornaAutomatico = ({ id, flag, label }) => {
    const invariato = firmaTesto(form[id]) === firmaTesto(righeAHtml(generati[id], true));
    if (invariato) {
      aggiorna({ [flag]: true });
      return;
    }
    setConferma({
      titolo: "Tornare al testo automatico?",
      descrizione: `Le modifiche fatte a mano a «${label}» andranno perse: il testo sarà ricomposto dai dati economici.`,
      etichetta: "Torna automatico",
      azione: () => aggiorna({ [flag]: true }),
    });
  };

  // Importi dei dati economici che il testo manuale non cita, né collegati
  // né scritti per esteso.
  const importiMancanti = (id) => {
    const testo = testoVisibile(form[id]);
    const citati = new Set(chiaviSegnaposti(form[id]));
    return generati[id]
      .filter((r) => r.tok && !citati.has(r.chiave) && !testo.includes(r.tok))
      .map((r) => r.tok);
  };
  // Importi che un testo a mano può citare, per il menu «Importo» e i chip.
  const segnaposti = useMemo(() => [
    { chiave: SEGNAPOSTO_ATTIVAZIONE, etichetta: form.nome_attivazione.trim() || "Attivazione", valore: eur2(numeroOZero(form.importo_attivazione)) },
    ...form.addons.map((a) => ({
      chiave: a.addon_id,
      etichetta: a.label.trim() || "Addon senza nome",
      valore: a.tipo_valore === "percentuale" ? pct(numeroOZero(a.valore)) : eur2(numeroOZero(a.valore)),
    })),
  ], [form.importo_attivazione, form.nome_attivazione, form.addons]);

  // ---- Condizioni generali ----

  const condizioniStandard = !testoVisibile(form.condizioni_generali) || form.condizioni_generali === standard;
  const ripristinaCondizioni = () => {
    setConferma({
      titolo: "Ripristinare il testo standard?",
      descrizione: "Le modifiche alle Condizioni Generali di questo servizio andranno perse.",
      etichetta: "Ripristina",
      azione: () => aggiorna({ condizioni_generali: standard }),
    });
  };
  // Svuotato: si ripristina senza chiedere, non c'è nulla da perdere.
  const ripristinaOChiedi = () => (testoVisibile(form.condizioni_generali) ? ripristinaCondizioni() : aggiorna({ condizioni_generali: standard }));

  // ---- Chiusura e salvataggio ----

  const chiudi = () => {
    if (!modificato || salvando) {
      if (!salvando) onClose();
      return;
    }
    setConferma({
      titolo: "Chiudere senza salvare?",
      descrizione: "Le modifiche al servizio non ancora salvate andranno perse.",
      etichetta: "Chiudi senza salvare",
      azione: onClose,
    });
  };

  const salva = async () => {
    setTentato(true);
    if (errori.sezioni.size) {
      const etichette = ORDINE.filter((s) => errori.sezioni.has(s.id)).map((s) => s.label);
      toast.error(`Da completare: ${etichette.join(", ")}`);
      setSezione(ORDINE.find((s) => errori.sezioni.has(s.id)).id);
      const inErrore = form.task.find((v) => errori.campi[`giorni-${v.task_key}`]);
      if (inErrore) setApertaKey(inErrore.task_key);
      return;
    }
    setSalvando(true);
    try {
      const payload = {
        nome: form.nome.trim(),
        descrizione: form.descrizione.trim(),
        importo_attivazione: attivazione,
        nome_attivazione: form.nome_attivazione.trim(),
        primo_mese_solo_attivazione: form.primo_mese_solo_attivazione,
        addons: addonOrdinati(form.addons).map((a) => ({
          addon_id: a.addon_id,
          label: a.label.trim(),
          ricorrenza: a.ricorrenza,
          tipo_valore: a.tipo_valore,
          valore: numeroOZero(a.valore),
          attivazione: !!a.attivazione,
        })),
        ...Object.fromEntries(TESTI_LIBERI.map(({ id }) => [id, form[id] || ""])),
        // In automatico il backend rigenera comunque il testo: si manda
        // l'anteprima perché la validazione dei testi obbligatori resti una sola.
        ...Object.fromEntries(TESTI_AUTO.flatMap(({ id, flag }) => [
          [id, form[flag] ? righeAHtml(generati[id]) : form[id] || ""],
          [flag, form[flag]],
        ])),
        // Il testo standard non modificato si salva vuoto: il servizio segue
        // così eventuali aggiornamenti dello standard.
        condizioni_generali: condizioniStandard ? "" : form.condizioni_generali,
        task: form.task.map((v) => ({
          task_key: v.task_key,
          tipo_scadenza: v.tipo_scadenza,
          giorni: v.tipo_scadenza === SCAD_GIORNI
            ? Number(v.giorni) * (v.verso === "prima" ? -1 : 1)
            : v.tipo_scadenza === SCAD_ATTIVAZIONE ? Number(v.giorni) : 0,
          settimana: v.settimana,
          giorno_settimana: v.giorno_settimana,
          ruoli: v.ruoli,
        })),
      };
      const res = await apiFetch(
        servizio ? `/catalogo-servizi/${servizio.servizio_id}` : "/catalogo-servizi",
        {
          method: servizio ? "PUT" : "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(servizio ? payload : { ...payload, ambito }),
        }
      );
      if (res.ok) {
        const salvato = await res.json().catch(() => ({}));
        toast.success(servizio ? "Servizio aggiornato" : "Servizio creato");
        onSaved(salvato);
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(typeof err.detail === "string" ? err.detail : "Errore nel salvataggio del servizio");
      }
    } catch {
      toast.error("Errore nel salvataggio del servizio");
    } finally {
      setSalvando(false);
    }
  };

  // ---- Navigazione ----

  const metaSezione = (id) => {
    if (id === SEZ_SERVIZIO) {
      if (attivazioniExtra.length) {
        return { testo: `${attivazioniExtra.length + 1} attivazioni · ${addonNormali.length} addon` };
      }
      return { testo: `${form.addons.length} addon · ${formatCurrency(attivazione)} attivazione` };
    }
    if (id === SEZ_TASK) return { testo: plurale(form.task.length, "task", "task") };
    if (id === SEZ_CONDIZIONI) return condizioniStandard ? { testo: "Standard" } : { testo: "Personalizzate", accento: true };
    const auto = TESTI_AUTO.find((t) => t.id === id);
    if (auto) return form[auto.flag] ? { testo: "Automatico", accento: true } : { testo: "Manuale" };
    return { testo: contaVoci(form[id]) };
  };

  const indice = ORDINE.findIndex((s) => s.id === sezione);
  const successiva = ORDINE[indice + 1];
  const erroreCampo = (chiave) => tentato && errori.campi[chiave];

  return (
    <>
      <Dialog open={open} onOpenChange={(v) => { if (!v) chiudi(); }}>
        <DialogContent
          onEscapeKeyDown={(e) => {
            if (rinomina) { e.preventDefault(); setRinomina(null); }
            else if (apertaKey) { e.preventDefault(); setApertaKey(null); }
          }}
          className="scheda-font-ui w-[calc(100vw-32px)] max-w-[1120px] sm:max-w-[1120px] h-[min(840px,calc(100vh-64px))] p-0 gap-0 border-0 overflow-hidden flex flex-col rounded-[20px] sm:rounded-[20px] shadow-[0_30px_80px_-30px_rgba(20,20,60,.35)] [&>button:last-child]:hidden"
          data-testid="servizio-dialog"
        >
          {/* Header */}
          <div className="shrink-0 flex items-center gap-3.5 px-6 py-5 border-b border-[#eeeef5]">
            <div className="w-10 h-10 rounded-[12px] bg-[#eeeefc] flex items-center justify-center shrink-0">
              <Package className="w-5 h-5 text-[#5b5bd6]" strokeWidth={1.8} />
            </div>
            <div className="flex-1 min-w-0 flex flex-col gap-[3px]">
              <DialogTitle className="text-[19px] font-extrabold leading-[1.2] tracking-normal text-[#15161c]">
                {servizio ? "Modifica servizio" : ambito === "eventi" ? "Nuovo servizio eventi" : "Nuovo servizio"}
              </DialogTitle>
              <DialogDescription className="text-[13px] font-medium leading-[1.3] text-[#6b7280]">
                Dati del servizio e testi della Proposta di Adesione
              </DialogDescription>
            </div>
            <button
              type="button"
              onClick={chiudi}
              aria-label="Chiudi"
              className="w-9 h-9 rounded-[10px] text-[#6b7280] hover:bg-[#f3f3f8] flex items-center justify-center shrink-0 transition-colors"
              data-testid="servizio-dialog-chiudi"
            >
              <X className="w-[18px] h-[18px]" strokeWidth={2} />
            </button>
          </div>

          {/* Body */}
          <div className="flex-1 min-h-0 flex flex-col md:grid md:grid-cols-[260px_minmax(0,1fr)]">
            <nav
              className="shrink-0 bg-[#f8f8fc] border-b md:border-b-0 md:border-r border-[#eeeef5] px-3 py-2 md:py-[18px] flex md:flex-col gap-1 md:gap-[18px] overflow-x-auto md:overflow-y-auto [scrollbar-width:none]"
              aria-label="Sezioni del servizio"
            >
              {GRUPPI.map((gruppo) => (
                <div key={gruppo.titolo} className="flex md:flex-col gap-1 md:gap-[3px] shrink-0">
                  <span className="hidden md:block px-3 pb-2 text-[10.5px] font-bold leading-none uppercase tracking-[.1em] text-[#6b7280]">
                    {gruppo.titolo}
                  </span>
                  {gruppo.sezioni.map((s) => {
                    const attiva = sezione === s.id;
                    const meta = metaSezione(s.id);
                    const inErrore = tentato && errori.sezioni.has(s.id);
                    return (
                      <button
                        key={s.id}
                        type="button"
                        onClick={() => setSezione(s.id)}
                        aria-current={attiva ? "step" : undefined}
                        className={`shrink-0 flex items-center gap-[11px] px-3 py-2.5 rounded-[11px] text-left transition-[background-color,box-shadow] duration-200 ${
                          attiva ? "bg-white shadow-[0_1px_2px_rgba(20,20,50,.06),0_0_0_1px_#e6e6f2]" : "hover:bg-[#f1f1f7]"
                        }`}
                        data-testid={`servizio-tab-${s.id}`}
                      >
                        <span className={`relative w-6 h-6 rounded-full flex items-center justify-center text-[11.5px] font-bold scheda-font-mono shrink-0 ${
                          attiva ? "bg-[#5b5bd6] text-white" : "bg-[#ececf3] text-[#565d6d]"
                        }`}>
                          {ORDINE.indexOf(s) + 1}
                          {inErrore && (
                            <span className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-[#c0392b] ring-2 ring-[#f8f8fc]" aria-label="da completare" />
                          )}
                        </span>
                        <span className="min-w-0 flex flex-col gap-[3px]">
                          <span className={`text-[13.5px] font-semibold leading-[1.2] whitespace-nowrap ${attiva ? "text-[#15161c]" : "text-[#3c414d]"}`}>{s.label}</span>
                          <span className={`hidden md:block text-[12px] font-medium leading-[1.2] ${meta.accento ? "text-[#4a4ac4]" : "text-[#6b7280]"}`}>
                            {meta.testo}
                          </span>
                        </span>
                      </button>
                    );
                  })}
                </div>
              ))}
            </nav>

            <div className="min-h-0 flex-1 overflow-y-auto px-5 md:px-8 pt-[26px] pb-8">
              {/* Le sezioni non attive restano montate (solo nascoste): gli editor
                  conservano annulla/ripeti e cursore. Tornando visibili ripartono
                  dall'animazione d'ingresso. */}

              {/* 1 · Servizio e prezzi */}
              <div className={sezione === SEZ_SERVIZIO ? "scheda-row-anim flex flex-col gap-[22px]" : "hidden"}>
                <div className="flex flex-col gap-4">
                  <label className="flex flex-col gap-[7px]">
                    <span className={ETICHETTA_CAMPO}>Nome del servizio</span>
                    <input
                      type="text"
                      value={form.nome}
                      onChange={(e) => aggiorna({ nome: e.target.value })}
                      className={`h-11 px-3.5 rounded-[11px] border border-[#e4e4ed] text-[15px] font-semibold text-[#15161c] outline-none transition-[border-color,box-shadow] ${FOCUS} ${erroreCampo("nome") ? BORDO_ERRORE : ""}`}
                      data-testid="servizio-nome-input"
                    />
                  </label>
                  <label className="flex flex-col gap-[7px]">
                    <span className={ETICHETTA_CAMPO}>Descrizione</span>
                    <textarea
                      rows={2}
                      value={form.descrizione}
                      onChange={(e) => aggiorna({ descrizione: e.target.value })}
                      className={`px-3.5 py-[11px] rounded-[11px] border border-[#e4e4ed] text-[14.5px] font-medium leading-[1.55] text-[#15161c] outline-none resize-none transition-[border-color,box-shadow] ${FOCUS} ${erroreCampo("descrizione") ? BORDO_ERRORE : ""}`}
                      data-testid="servizio-descrizione-input"
                    />
                  </label>
                  <div className="flex flex-col gap-1.5 px-3.5 py-3 rounded-[12px] bg-[#f8f8fc] border border-[#eeeef5]">
                    <span className="text-[11.5px] font-semibold leading-[1.3] text-[#6b7280]">
                      Nel contratto, alla voce «Servizi cui intende aderire»
                    </span>
                    <span className="text-[13.5px] font-medium leading-[1.55] text-[#3c414d] [text-wrap:pretty]" data-testid="servizio-anteprima-voce">
                      «{form.descrizione.replace(/\s+/g, " ").trim().replace(/\.+$/, "") || "Descrizione"} per la durata minima di N mesi».
                    </span>
                    {/durata/i.test(form.descrizione) && (
                      <span className="flex items-center gap-[7px] text-[12.5px] font-semibold leading-[1.4] text-[#8a6414]" data-testid="servizio-avviso-durata">
                        <TriangleAlert className="w-3.5 h-3.5 shrink-0" strokeWidth={2} />
                        La descrizione indica già una durata: nel contratto comparirebbe due volte.
                      </span>
                    )}
                  </div>
                </div>

                <div className="flex flex-col gap-3">
                  <div className="flex flex-wrap items-baseline gap-x-2.5 gap-y-1">
                    <h3 className="text-[15px] font-bold leading-[1.2] text-[#15161c]">Dati economici</h3>
                    <span className="text-[12.5px] font-medium text-[#6b7280]">Alimentano Corrispettivo e Modalità di pagamento</span>
                  </div>
                  <div className="rounded-[14px] border border-[#e9e9f1] overflow-hidden">
                    <div className={`hidden sm:grid ${COLONNE_ECONOMICI} gap-3 px-3.5 py-2.5 bg-[#fbfbfd] border-b border-[#eeeef5] text-[10.5px] font-bold leading-none uppercase tracking-[.09em] text-[#6b7280]`}>
                      <span>Voce</span><span>Ricorrenza</span><span>Importo</span><span />
                    </div>

                    {/* Attivazione: fissa, non eliminabile. Nei servizi eventi ha un
                        nome e se ne possono aggiungere altre (sotto). */}
                    <div className={`grid grid-cols-2 ${COLONNE_ECONOMICI} gap-3 items-center px-3.5 py-2.5`}>
                      {eventi ? (
                        <input
                          type="text"
                          value={form.nome_attivazione}
                          onChange={(e) => aggiorna({ nome_attivazione: e.target.value })}
                          placeholder="Attivazione"
                          maxLength={80}
                          className={`h-[38px] px-3 rounded-[10px] border border-transparent hover:border-[#e4e4ed] bg-white text-[14px] font-bold text-[#15161c] placeholder:text-[#15161c] outline-none transition-[border-color,box-shadow] ${FOCUS}`}
                          data-testid="servizio-nome-attivazione-input"
                        />
                      ) : (
                        <span className="text-[14px] font-bold leading-none text-[#15161c] pl-0.5">Attivazione</span>
                      )}
                      <span className="justify-self-end sm:justify-self-start inline-flex items-center h-7 px-2.5 rounded-[8px] bg-[#fdf3dd] text-[12.5px] font-semibold text-[#8a6414]">
                        Una tantum
                      </span>
                      <div className={`col-span-2 sm:col-span-1 flex items-center h-[38px] px-3 rounded-[10px] border border-[#e4e4ed] transition-[border-color,box-shadow] ${FOCUS_WITHIN} ${erroreCampo("importo") ? BORDO_ERRORE : ""}`}>
                        <input
                          type="text"
                          inputMode="decimal"
                          value={form.importo_attivazione}
                          onChange={(e) => aggiorna({ importo_attivazione: e.target.value })}
                          placeholder="0"
                          className="flex-1 min-w-0 border-none outline-none bg-transparent text-[14px] font-semibold text-[#15161c] scheda-font-mono"
                          data-testid="servizio-importo-input"
                        />
                        <span className="text-[13px] font-semibold text-[#6b7280] scheda-font-mono">€</span>
                      </div>
                      <span className="hidden sm:block" />
                    </div>
                    <label className="flex flex-wrap items-center gap-x-3 gap-y-1 mx-3.5 mb-2.5 px-3 py-2.5 rounded-[10px] bg-[#f8f8fc] cursor-pointer">
                      <Switch
                        checked={form.primo_mese_solo_attivazione}
                        onCheckedChange={(v) => aggiorna({ primo_mese_solo_attivazione: v })}
                        className="h-[22px] w-[38px] data-[state=checked]:bg-[#5b5bd6] data-[state=unchecked]:bg-[#d4d4e2]"
                        data-testid="servizio-primo-mese-toggle"
                      />
                      <span className="text-[13px] font-semibold leading-[1.4] text-[#15161c]">Primo mese solo attivazione</span>
                      <span className="text-[12.5px] font-medium leading-[1.4] text-[#6b7280]">gli addon mensili partono dal mese successivo</span>
                    </label>

                    {/* Attivazioni successive (servizi eventi): una tantum, si
                        registrano una per una nei Pagamenti. È la prima ad avviare il cliente. */}
                    {attivazioniExtra.map((a, i) => (
                      <div
                        key={a.chiave}
                        className={`grid grid-cols-[minmax(0,1fr)_36px] ${COLONNE_ECONOMICI} gap-3 items-center px-3.5 py-2.5 border-t border-[#eeeef5]`}
                        data-testid={`attivazione-row-${i}`}
                      >
                        <input
                          type="text"
                          value={a.label}
                          onChange={(e) => aggiornaAddon(a.chiave, { label: e.target.value })}
                          placeholder="Nome attivazione (es. Saldo)"
                          maxLength={80}
                          className={`h-[38px] px-3 rounded-[10px] border border-transparent hover:border-[#e4e4ed] bg-white text-[14px] font-bold text-[#15161c] placeholder:text-[#9095a1] placeholder:font-medium outline-none transition-[border-color,box-shadow] ${FOCUS} ${erroreCampo(`label-${a.chiave}`) ? BORDO_ERRORE : ""}`}
                          data-testid={`attivazione-nome-${i}`}
                        />
                        <button
                          type="button"
                          onClick={() => rimuoviAddon(a.chiave)}
                          aria-label="Rimuovi attivazione"
                          className="sm:order-last w-[34px] h-[34px] rounded-[9px] text-[#9095a1] hover:bg-[#fdecea] hover:text-[#c0392b] flex items-center justify-center transition-colors"
                          data-testid={`rimuovi-attivazione-${i}`}
                        >
                          <X className="w-[15px] h-[15px]" strokeWidth={2} />
                        </button>
                        <span className="max-sm:col-span-2 justify-self-start inline-flex items-center h-7 px-2.5 rounded-[8px] bg-[#fdf3dd] text-[12.5px] font-semibold text-[#8a6414]">
                          Una tantum
                        </span>
                        <div className={`max-sm:col-span-2 flex items-center h-[38px] px-3 rounded-[10px] border border-[#e4e4ed] transition-[border-color,box-shadow] ${FOCUS_WITHIN} ${erroreCampo(`valore-${a.chiave}`) ? BORDO_ERRORE : ""}`}>
                          <input
                            type="text"
                            inputMode="decimal"
                            value={a.valore}
                            onChange={(e) => aggiornaAddon(a.chiave, { valore: e.target.value })}
                            placeholder="0"
                            className="flex-1 min-w-0 border-none outline-none bg-transparent text-[14px] font-semibold text-[#15161c] scheda-font-mono"
                            data-testid={`attivazione-importo-${i}`}
                          />
                          <span className="text-[13px] font-semibold text-[#6b7280] scheda-font-mono">€</span>
                        </div>
                      </div>
                    ))}
                    {eventi && (
                      <button
                        type="button"
                        onClick={aggiungiAttivazione}
                        className="w-full flex items-center gap-2 px-4 py-[13px] border-t border-dashed border-[#e0e0ea] bg-[#fbfbfd] hover:bg-[#fdf8ec] text-[13px] font-semibold text-[#8a6414] transition-colors"
                        data-testid="aggiungi-attivazione-btn"
                      >
                        <Plus className="w-[15px] h-[15px]" strokeWidth={2.1} />
                        Aggiungi attivazione
                      </button>
                    )}

                    {addonNormali.map((a, i) => (
                      <div
                        key={a.chiave}
                        className={`grid grid-cols-[minmax(0,1fr)_36px] ${COLONNE_ECONOMICI} gap-3 items-center px-3.5 py-2.5 border-t border-[#eeeef5]`}
                        data-testid={`addon-row-${i}`}
                      >
                        <input
                          type="text"
                          value={a.label}
                          onChange={(e) => aggiornaAddon(a.chiave, { label: e.target.value })}
                          placeholder="Descrizione addon"
                          className={`h-[38px] px-3 rounded-[10px] border border-transparent hover:border-[#e4e4ed] bg-white text-[14px] font-semibold text-[#15161c] placeholder:text-[#9095a1] placeholder:font-medium outline-none transition-[border-color,box-shadow] ${FOCUS} ${erroreCampo(`label-${a.chiave}`) ? BORDO_ERRORE : ""}`}
                          data-testid={`addon-label-${i}`}
                        />
                        <button
                          type="button"
                          onClick={() => rimuoviAddon(a.chiave)}
                          title="Rimuovi"
                          aria-label="Rimuovi addon"
                          className="sm:order-last w-[34px] h-[34px] rounded-[9px] text-[#9095a1] hover:bg-[#fdecea] hover:text-[#c0392b] flex items-center justify-center transition-colors"
                          data-testid={`rimuovi-addon-${i}`}
                        >
                          <X className="w-[15px] h-[15px]" strokeWidth={2} />
                        </button>
                        <div className="max-sm:col-span-2">
                          <Segmentato
                            opzioni={[{ valore: "una_tantum", label: "Una tantum" }, { valore: "mensile", label: "Mensile" }]}
                            valore={a.ricorrenza}
                            onChange={(v) => aggiornaAddon(a.chiave, { ricorrenza: v })}
                            testIdPrefix={`addon-ricorrenza-${i}`}
                          />
                        </div>
                        <div className={`max-sm:col-span-2 flex items-center gap-1.5 h-[38px] pl-3 pr-1 rounded-[10px] border border-[#e4e4ed] transition-[border-color,box-shadow] ${FOCUS_WITHIN} ${erroreCampo(`valore-${a.chiave}`) ? BORDO_ERRORE : ""}`}>
                          <input
                            type="text"
                            inputMode="decimal"
                            value={a.valore}
                            onChange={(e) => aggiornaAddon(a.chiave, { valore: e.target.value })}
                            placeholder="0"
                            className="flex-1 min-w-0 border-none outline-none bg-transparent text-[14px] font-semibold text-[#15161c] scheda-font-mono"
                            data-testid={`addon-valore-${i}`}
                          />
                          <Segmentato
                            piccolo
                            opzioni={[{ valore: "importo", label: "€" }, { valore: "percentuale", label: "%" }]}
                            valore={a.tipo_valore}
                            onChange={(v) => aggiornaAddon(a.chiave, { tipo_valore: v })}
                            testIdPrefix={`addon-tipo-valore-${i}`}
                          />
                        </div>
                      </div>
                    ))}

                    <button
                      type="button"
                      onClick={aggiungiAddon}
                      className="w-full flex items-center gap-2 px-4 py-[13px] border-t border-dashed border-[#e0e0ea] bg-[#fbfbfd] hover:bg-[#f4f4fe] text-[13px] font-semibold text-[#5b5bd6] transition-colors"
                      data-testid="aggiungi-addon-btn"
                    >
                      <Plus className="w-[15px] h-[15px]" strokeWidth={2.1} />
                      Aggiungi addon
                    </button>
                  </div>

                  <div className="flex flex-wrap gap-2.5">
                    <span className="inline-flex items-center gap-2 h-8 px-3 rounded-[9px] bg-[#fdf3dd] text-[12.5px] font-medium text-[#7a4e07]" data-testid="servizio-riepilogo-firma">
                      Alla firma <strong className="text-[13px] font-semibold scheda-font-mono">{formatCurrency(allaFirma)}</strong>
                    </span>
                    <span className="inline-flex items-center gap-2 h-8 px-3 rounded-[9px] bg-[#eeeefc] text-[12.5px] font-medium text-[#3f3fb0]" data-testid="servizio-riepilogo-mese">
                      {form.primo_mese_solo_attivazione ? "Dal 2° mese" : "Ogni mese"}
                      <strong className="text-[13px] font-semibold scheda-font-mono">{ogniMese ? `${ogniMese} / mese` : "—"}</strong>
                    </span>
                  </div>
                </div>
              </div>

              {/* 2 · Task e risorse — lista a righe, una aperta alla volta (design_handoff_task_risorse, 11a) */}
              <div className={sezione === SEZ_TASK ? "scheda-row-anim flex flex-col gap-3.5" : "hidden"}>
                <IntestazioneSezione titolo="Task e risorse">
                  Le scadenze si calcolano dalla Data Girato, oppure dal pagamento dell&apos;attivazione per le task
                  «all&apos;attivazione». Le modifiche valgono dalla prossima generazione: le task già create restano come sono.
                </IntestazioneSezione>

                {form.task.length === 0 ? (
                  <div className="px-4 py-5 rounded-[14px] border border-dashed border-[#e0e0ea] text-center text-[13px] font-medium text-[#6b7280]" data-testid="servizio-task-vuoto">
                    Nessuna task: i clienti con questo servizio non ne riceveranno dalla Data Girato.
                  </div>
                ) : (
                  <div className="rounded-[14px] border border-[#e9e9f1]" data-testid="servizio-task-lista">
                    {form.task.map((v, i) => {
                      const aperta = apertaKey === v.task_key;
                      const storica = perChiave[v.task_key]?.storica !== false;
                      const inRinomina = rinomina?.task_key === v.task_key;
                      const esempio = esempioScadenza(v);
                      const alterna = () => { setRinomina(null); setApertaKey(aperta ? null : v.task_key); };
                      return (
                        <div
                          key={v.task_key}
                          className={`relative transition-[box-shadow,background-color] duration-200 ${i ? "border-t border-[#f0f0f6]" : ""} ${
                            aperta ? `bg-white rounded-[12px] z-[1] ${OMBRA_RIGA_APERTA}` : ""
                          }`}
                          data-testid={`servizio-task-${v.task_key}`}
                        >
                          {/* Non un <button>: nella riga aperta ospita la matita
                              della rinomina, e i bottoni non si annidano. */}
                          <div
                            role="button"
                            tabIndex={0}
                            aria-expanded={aperta}
                            onClick={inRinomina ? undefined : alterna}
                            onKeyDown={(e) => {
                              if (inRinomina || e.target !== e.currentTarget) return;
                              if (e.key === "Enter" || e.key === " ") { e.preventDefault(); alterna(); }
                            }}
                            className={`w-full grid grid-cols-[minmax(0,1fr)_auto_28px] gap-3.5 items-center px-4 py-3.5 text-left cursor-pointer outline-none rounded-[12px] focus-visible:shadow-[0_0_0_3px_rgba(91,91,214,.14)] ${aperta ? "" : "hover:bg-[#fbfbfd]"}`}
                            data-testid={`servizio-task-${v.task_key}-riga`}
                          >
                            <span className="min-w-0 flex flex-col gap-[3px]">
                              {inRinomina ? (
                                <input
                                  autoFocus
                                  type="text"
                                  value={rinomina.nome}
                                  onClick={(e) => e.stopPropagation()}
                                  onChange={(e) => setRinomina({ ...rinomina, nome: e.target.value })}
                                  onBlur={confermaRinomina}
                                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); confermaRinomina(); } }}
                                  maxLength={80}
                                  className={`h-8 px-2.5 rounded-[9px] border border-[#e4e4ed] text-[14px] font-bold text-[#15161c] outline-none transition-[border-color,box-shadow] ${FOCUS}`}
                                  data-testid={`servizio-task-${v.task_key}-rinomina-input`}
                                />
                              ) : (
                                <span className="flex items-center gap-1.5 min-w-0">
                                  <span className="text-[14px] font-bold leading-[1.3] text-[#15161c] truncate">{nomeTask(v.task_key)}</span>
                                  {aperta && !storica && (
                                    <InfoTooltip title="Rinomina" description="Il nome cambia in tutti i servizi e nelle task già generate.">
                                      <button
                                        type="button"
                                        onClick={(e) => { e.stopPropagation(); setRinomina({ task_key: v.task_key, nome: nomeTask(v.task_key) }); }}
                                        aria-label="Rinomina la task"
                                        className="w-6 h-6 rounded-[7px] text-[#9095a1] hover:text-[#5b5bd6] hover:bg-[#f4f4fe] flex items-center justify-center shrink-0 transition-colors"
                                        data-testid={`servizio-task-${v.task_key}-rinomina`}
                                      >
                                        <Pencil className="w-[13px] h-[13px]" strokeWidth={2} />
                                      </button>
                                    </InfoTooltip>
                                  )}
                                </span>
                              )}
                              <span className="flex items-center gap-1.5 text-[12.5px] font-medium leading-[1.3] text-[#565d6d]" data-testid={`servizio-task-${v.task_key}-scadenza`}>
                                <Clock className="w-[13px] h-[13px] shrink-0" strokeWidth={2} />
                                {descriviScadenza(v)}
                              </span>
                            </span>
                            <span className="flex flex-wrap justify-end gap-1">
                              {v.ruoli.length ? (
                                RUOLI_TASK.filter((r) => v.ruoli.includes(r.id)).map((r) => (
                                  <span key={r.id} className="inline-flex items-center h-[22px] px-2 rounded-[6px] bg-[#eeeefc] text-[11.5px] font-semibold text-[#3f3fb0]">
                                    {r.sigla}
                                  </span>
                                ))
                              ) : (
                                <span className="inline-flex items-center h-[22px] px-2 rounded-[6px] bg-[#fef3c7] text-[11.5px] font-semibold text-[#b45309]">
                                  Solo PM e amm.
                                </span>
                              )}
                            </span>
                            <span className={`w-7 h-7 rounded-[8px] flex items-center justify-center text-[#9095a1] transition-transform duration-200 ${aperta ? "rotate-180" : ""}`}>
                              <ChevronDown className="w-4 h-4" strokeWidth={2} />
                            </span>
                          </div>

                          {aperta && (
                            <div className="px-4 pb-4 pt-0.5 flex flex-col gap-3.5 scheda-drop-anim">
                              <div className="flex flex-col gap-2 p-3.5 rounded-[12px] bg-[#fbfbfd] border border-[#eeeef4]">
                                <div className="flex flex-wrap items-center gap-2.5">
                                  <span className={ETICHETTA_BLOCCO}>Scadenza</span>
                                  <div className="flex-1" />
                                  <div className="flex gap-0.5 p-[3px] bg-[#eeeef4] rounded-[9px]">
                                    {[
                                      { valore: SCAD_GIORNI, label: "Giorni dal girato" },
                                      { valore: SCAD_GIORNO_MESE, label: "Giorno fisso del mese" },
                                      { valore: SCAD_ATTIVAZIONE, label: "All'attivazione" },
                                    ].map((o) => {
                                      const attivo = v.tipo_scadenza === o.valore;
                                      return (
                                        <button
                                          key={o.valore}
                                          type="button"
                                          onClick={() => aggiornaTask(v.task_key, { tipo_scadenza: o.valore })}
                                          aria-pressed={attivo}
                                          style={attivo ? { boxShadow: SHADOWS.appoggiato } : undefined}
                                          className={`h-[26px] px-2.5 rounded-[7px] text-[12px] font-semibold transition-colors ${attivo ? "bg-white text-[#15161c]" : "text-[#565d6d] hover:text-[#15161c]"}`}
                                          data-testid={`servizio-task-${v.task_key}-tipo-${o.valore}`}
                                        >
                                          {o.label}
                                        </button>
                                      );
                                    })}
                                  </div>
                                </div>

                                {contaGiorni(v) ? (
                                  <div className="flex flex-wrap items-center gap-2 text-[14px] font-medium text-[#3c414d]">
                                    <span>Scade</span>
                                    <input
                                      type="text"
                                      inputMode="numeric"
                                      value={v.giorni}
                                      onChange={(e) => aggiornaTask(v.task_key, { giorni: e.target.value })}
                                      aria-label="Giorni lavorativi"
                                      className={`w-[52px] h-[34px] px-1.5 rounded-[9px] border bg-white text-center text-[14px] font-semibold text-[#15161c] scheda-font-mono outline-none transition-[border-color,box-shadow,transform] focus:-translate-y-px ${FOCUS} ${
                                        giorniValidi(v) ? "border-[#e4e4ed]" : "border-[#e05a4a]"
                                      } ${erroreCampo(`giorni-${v.task_key}`) ? BORDO_ERRORE : ""}`}
                                      data-testid={`servizio-task-${v.task_key}-giorni`}
                                    />
                                    <span>giorni lavorativi</span>
                                    {v.tipo_scadenza === SCAD_ATTIVAZIONE ? (
                                      <span>dopo il pagamento dell&apos;attivazione</span>
                                    ) : (
                                      <>
                                        <SelectFrase
                                          value={v.verso}
                                          onChange={(val) => aggiornaTask(v.task_key, { verso: val })}
                                          opzioni={[{ valore: "dopo", label: "dopo" }, { valore: "prima", label: "prima" }]}
                                          label="Prima o dopo il girato"
                                          testId={`servizio-task-${v.task_key}-verso`}
                                        />
                                        <span>il girato</span>
                                      </>
                                    )}
                                  </div>
                                ) : (
                                  <div className="flex flex-wrap items-center gap-2 text-[14px] font-medium text-[#3c414d]">
                                    <span>Scade il</span>
                                    <SelectFrase
                                      value={v.settimana}
                                      onChange={(val) => aggiornaTask(v.task_key, { settimana: Number(val) })}
                                      opzioni={SETTIMANE}
                                      label="Settimana del mese"
                                      testId={`servizio-task-${v.task_key}-settimana`}
                                    />
                                    <SelectFrase
                                      value={v.giorno_settimana}
                                      onChange={(val) => aggiornaTask(v.task_key, { giorno_settimana: Number(val) })}
                                      opzioni={GIORNI_SETTIMANA.map((g) => ({ valore: g.valore, label: g.nome }))}
                                      label="Giorno della settimana"
                                      testId={`servizio-task-${v.task_key}-giorno`}
                                    />
                                    <span>del mese</span>
                                  </div>
                                )}

                                <span
                                  className={`flex items-center gap-1.5 text-[12px] font-medium ${esempio ? "text-[#9095a1]" : "text-[#c0392b]"}`}
                                  data-testid={`servizio-task-${v.task_key}-esempio`}
                                >
                                  <Calendar className="w-3 h-3 shrink-0" strokeWidth={2.2} />
                                  {esempio || `Indica da 0 a ${MAX_GIORNI} giorni lavorativi`}
                                </span>
                              </div>

                              <div className="flex flex-col gap-2">
                                <span className={ETICHETTA_BLOCCO}>Chi la gestisce</span>
                                {/* Cinque ruoli: a griglia fissa «Content Creator» si
                                    troncherebbe, così ogni bottone è largo quanto il suo nome. */}
                                <div className="flex flex-wrap gap-2">
                                  {RUOLI_TASK.map((ruolo) => {
                                    const on = v.ruoli.includes(ruolo.id);
                                    return (
                                      <button
                                        key={ruolo.id}
                                        type="button"
                                        onClick={() => alternaRuoloTask(v.task_key, ruolo.id)}
                                        aria-pressed={on}
                                        className={`flex-auto h-[38px] flex items-center gap-2 px-2.5 rounded-[10px] border text-[13px] font-semibold whitespace-nowrap transition-colors ${
                                          on ? "border-[#c9c9f0] bg-[#f7f7fe] text-[#3f3fb0]" : "border-[#e4e4ed] bg-white text-[#565d6d] hover:border-[#c9c9f0]"
                                        }`}
                                        data-testid={`servizio-task-${v.task_key}-${ruolo.id}`}
                                      >
                                        <span className={`shrink-0 w-4 h-4 rounded-[5px] border-[1.5px] flex items-center justify-center ${on ? "border-[#5b5bd6] bg-[#5b5bd6]" : "border-[#c9ccd6] bg-white"}`}>
                                          {on && <Check className="w-2.5 h-2.5 text-white" strokeWidth={3.2} />}
                                        </span>
                                        {ruolo.label}
                                      </button>
                                    );
                                  })}
                                </div>
                                {v.ruoli.length === 0 && (
                                  <span className="text-[12px] font-medium text-[#b45309]">
                                    Nessuna risorsa: la vedono solo PM e amministrazione.
                                  </span>
                                )}
                              </div>

                              <div className="flex items-center gap-2 pt-3 border-t border-[#f0f0f6]">
                                <InfoTooltip title="Togli dal servizio" description="La task resta in libreria, disponibile per gli altri servizi.">
                                  <button
                                    type="button"
                                    onClick={() => rimuoviTask(v.task_key)}
                                    className="h-8 flex items-center gap-1.5 px-2.5 rounded-[9px] text-[12.5px] font-semibold text-[#c0392b] hover:bg-[#fdecea] transition-colors"
                                    data-testid={`servizio-task-${v.task_key}-rimuovi`}
                                  >
                                    <Trash2 className="w-3.5 h-3.5" strokeWidth={2} />
                                    Togli dal servizio
                                  </button>
                                </InfoTooltip>
                                <div className="flex-1" />
                                <button
                                  type="button"
                                  onClick={() => setApertaKey(null)}
                                  className={`${SECONDARY_BTN} !h-[34px] !px-3.5 !text-[13px]`}
                                  data-testid={`servizio-task-${v.task_key}-fatto`}
                                >
                                  Fatto
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}

                <button
                  type="button"
                  onClick={() => setAggiungiAperto((a) => !a)}
                  aria-expanded={aggiungiAperto}
                  className="h-11 flex items-center justify-center gap-2 rounded-[12px] border border-dashed border-[#d4d4e0] bg-[#fbfbfd] text-[13.5px] font-semibold text-[#5b5bd6] hover:border-[#5b5bd6] hover:bg-[#f7f7fe] transition-colors"
                  data-testid="servizio-task-aggiungi-btn"
                >
                  <Plus className="w-[15px] h-[15px]" strokeWidth={2.4} />
                  Aggiungi task
                </button>

                {aggiungiAperto && (
                  <div className="flex flex-col gap-4 px-[18px] py-4 rounded-[14px] border border-[#e9e9f1] bg-white scheda-drop-anim" data-testid="servizio-task-aggiungi">
                    {disponibili.length > 0 && (
                      <div className="flex flex-col gap-2">
                        <span className={ETICHETTA_CAMPO}>Dalla libreria, condivisa fra i servizi</span>
                        <div className="flex flex-wrap gap-1.5">
                          {disponibili.map((t) => (
                            <span key={t.task_key} className="inline-flex items-center h-[30px] rounded-full border border-[#e0e0ea] bg-white overflow-hidden">
                              <button
                                type="button"
                                onClick={() => aggiungiTask(t)}
                                className="flex items-center gap-1.5 h-full pl-2.5 pr-[11px] text-[12.5px] font-semibold text-[#565d6d] hover:text-[#5b5bd6] hover:bg-[#f4f4fe] transition-colors"
                                data-testid={`servizio-task-libreria-${t.task_key}`}
                              >
                                <Plus className="w-[13px] h-[13px]" strokeWidth={2.4} />
                                {t.nome}
                              </button>
                              {!t.storica && (
                                <InfoTooltip title="Elimina dalla libreria" description="Possibile solo se nessun servizio la usa.">
                                  <button
                                    type="button"
                                    onClick={() => eliminaDaLibreria(t)}
                                    aria-label={`Elimina ${t.nome} dalla libreria`}
                                    className="flex items-center h-[30px] pl-1.5 pr-2.5 border-l border-[#eeeef5] text-[#9095a1] hover:text-[#c0392b] hover:bg-[#fdecea] transition-colors"
                                    data-testid={`servizio-task-libreria-${t.task_key}-elimina`}
                                  >
                                    <Trash2 className="w-[13px] h-[13px]" strokeWidth={2} />
                                  </button>
                                </InfoTooltip>
                              )}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    <div className="flex flex-col gap-2">
                      <span className={ETICHETTA_CAMPO}>Nuova task</span>
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={nuovaTask}
                          onChange={(e) => setNuovaTask(e.target.value)}
                          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); creaTask(); } }}
                          placeholder="Es. Shooting fotografico"
                          maxLength={80}
                          className={`flex-1 min-w-0 h-9 px-3 rounded-[10px] border border-[#e4e4ed] bg-white text-[13.5px] font-medium text-[#15161c] outline-none transition-[border-color,box-shadow] ${FOCUS}`}
                          data-testid="servizio-task-nuova-input"
                        />
                        <button
                          type="button"
                          onClick={creaTask}
                          disabled={!nuovaTask.trim() || creandoTask}
                          className={PRIMARY_BTN}
                          data-testid="servizio-task-nuova-crea"
                        >
                          {creandoTask ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" strokeWidth={2.2} />}
                          Crea e aggiungi
                        </button>
                      </div>
                      <span className="text-[12px] font-medium text-[#6b7280]">
                        Entra subito nella libreria: potrai aggiungerla anche agli altri servizi, ognuno con i suoi tempi.
                      </span>
                    </div>
                  </div>
                )}
              </div>

              {/* 3–5 · Testi liberi */}
              {TESTI_LIBERI.map((t) => (
                <div key={t.id} className={sezione === t.id ? "scheda-row-anim flex flex-col gap-4" : "hidden"}>
                  <IntestazioneSezione titolo={t.label}>
                    Stampato nella Proposta di Adesione sotto il titolo «{t.titolo}».
                  </IntestazioneSezione>
                  <EditorTestoRicco
                    alto
                    value={form[t.id]}
                    onChange={(v) => aggiorna({ [t.id]: v })}
                    testId={`servizio-${t.id.replace(/_/g, "-")}-editor`}
                  />
                </div>
              ))}

              {/* 6–7 · Testi generati dai dati economici */}
              {TESTI_AUTO.map((t) => {
                const auto = form[t.flag];
                const mancanti = auto ? [] : importiMancanti(t.id);
                return (
                  <div key={t.id} className={sezione === t.id ? "scheda-row-anim flex flex-col gap-4" : "hidden"}>
                    <IntestazioneSezione titolo={t.label}>
                      Stampato nella Proposta di Adesione sotto il titolo «{t.titolo}».
                    </IntestazioneSezione>
                    {auto ? (
                      <div className="flex flex-wrap items-center gap-3 px-3.5 py-3 rounded-[12px] bg-[#f4f4fe] border border-[#e0e0f8]">
                        <Link2 className="w-[17px] h-[17px] text-[#5b5bd6] shrink-0" strokeWidth={1.9} />
                        <span className="flex-1 min-w-[200px] text-[13px] font-medium leading-[1.45] text-[#3f3fb0]">
                          Generato dai dati economici. Gli importi evidenziati si aggiornano quando cambi le voci.
                        </span>
                        <button
                          type="button"
                          onClick={() => scriviAMano(t)}
                          className="h-8 px-3 rounded-[9px] border border-[#d7d7f2] bg-white text-[12.5px] font-semibold text-[#5b5bd6] hover:border-[#c9c9f0] transition-colors"
                          data-testid={`servizio-${t.id.replace(/_/g, "-")}-manuale`}
                        >
                          Scrivi a mano
                        </button>
                      </div>
                    ) : (
                      <div className="flex flex-wrap items-center gap-3 px-3.5 py-3 rounded-[12px] bg-[#fdf3dd] border border-[#ecd9ae]">
                        <span className="flex-1 min-w-[200px] flex flex-col gap-1 text-[13px] font-medium leading-[1.45] text-[#7a4e07]">
                          Testo scritto a mano. Gli importi inseriti col pulsante «Importo» restano collegati ai
                          dati economici e, nel contratto, a sconti e sovrapprezzi; quelli scritti per esteso no.
                          {errori.campi[`segnaposti-${t.id}`] && (
                            <span className="flex items-center gap-1.5 font-semibold text-[#c0392b]" data-testid={`servizio-${t.id.replace(/_/g, "-")}-voce-eliminata`}>
                              <TriangleAlert className="w-3.5 h-3.5 shrink-0" strokeWidth={2} />
                              Il testo cita l'importo di un addon eliminato: togli il chip rosso.
                            </span>
                          )}
                          {mancanti.length > 0 && (
                            <span className="flex items-center gap-1.5 font-semibold" data-testid={`servizio-${t.id.replace(/_/g, "-")}-disallineato`}>
                              <TriangleAlert className="w-3.5 h-3.5 shrink-0" strokeWidth={2} />
                              Non compaiono nel testo: {mancanti.join(", ")}
                            </span>
                          )}
                        </span>
                        <button
                          type="button"
                          onClick={() => tornaAutomatico(t)}
                          className="h-8 px-3 rounded-[9px] border border-[#ecd9ae] bg-white text-[12.5px] font-semibold text-[#8a6414] hover:border-[#dcc48e] transition-colors"
                          data-testid={`servizio-${t.id.replace(/_/g, "-")}-automatico`}
                        >
                          Torna automatico
                        </button>
                      </div>
                    )}
                    {auto ? (
                      <AnteprimaGenerata righe={generati[t.id]} testId={`servizio-${t.id.replace(/_/g, "-")}-anteprima`} />
                    ) : (
                      <EditorTestoRicco
                        alto
                        value={form[t.id]}
                        onChange={(v) => aggiorna({ [t.id]: v })}
                        segnaposti={segnaposti}
                        testId={`servizio-${t.id.replace(/_/g, "-")}-editor`}
                      />
                    )}
                  </div>
                );
              })}

              {/* 8 · Condizioni generali */}
              <div className={sezione === SEZ_CONDIZIONI ? "scheda-row-anim flex flex-col gap-4" : "hidden"}>
                <IntestazioneSezione titolo="Condizioni generali">
                  Stampate nel contratto sotto «Condizioni Generali di Contratto». Ogni titoletto è una clausola e
                  finisce nell'elenco delle clausole approvate dal cliente. Nel contratto {"{durata}"} diventa la durata,
                  {"{servizi}"} i servizi scelti, {"{dal}"} e {"{al}"} le date di inizio e scadenza.
                </IntestazioneSezione>
                {form.condizioni_generali === standard || (!standard && condizioniStandard) ? (
                  <div className="flex items-center gap-3 px-3.5 py-3 rounded-[12px] bg-[#f4f4fe] border border-[#e0e0f8]">
                    <Check className="w-[17px] h-[17px] text-[#5b5bd6] shrink-0" strokeWidth={2} />
                    <span className="flex-1 text-[13px] font-medium leading-[1.45] text-[#3f3fb0]">
                      Testo standard, uguale per tutti i servizi. Modificalo qui solo se questo servizio ha condizioni diverse.
                    </span>
                  </div>
                ) : (
                  <div className="flex flex-wrap items-center gap-3 px-3.5 py-3 rounded-[12px] bg-[#fdf3dd] border border-[#ecd9ae]">
                    <span className="flex-1 min-w-[200px] text-[13px] font-medium leading-[1.45] text-[#7a4e07]">
                      {condizioniStandard
                        ? "Campo vuoto: nel contratto andrà il testo standard."
                        : "Condizioni personalizzate per questo servizio. In un contratto con più servizi valgono quelle del primo servizio scelto."}
                    </span>
                    {standard && (
                      <button
                        type="button"
                        onClick={ripristinaOChiedi}
                        className="h-8 px-3 rounded-[9px] border border-[#ecd9ae] bg-white text-[12.5px] font-semibold text-[#8a6414] hover:border-[#dcc48e] transition-colors flex items-center gap-1.5"
                        data-testid="servizio-condizioni-generali-ripristina"
                      >
                        <RotateCcw className="w-3.5 h-3.5" strokeWidth={2} />
                        Ripristina standard
                      </button>
                    )}
                  </div>
                )}
                <EditorTestoRicco
                  alto
                  value={form.condizioni_generali}
                  onChange={(v) => aggiorna({ condizioni_generali: v })}
                  testId="servizio-condizioni-generali-editor"
                />
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="shrink-0 flex items-center gap-2.5 px-6 py-3.5 border-t border-[#eeeef5]">
            {successiva && (
              <button
                type="button"
                onClick={() => setSezione(successiva.id)}
                className="hidden sm:flex items-center gap-2 h-10 px-3.5 rounded-[11px] text-[13.5px] font-semibold text-[#5b5bd6] hover:bg-[#f4f4fe] transition-colors"
                data-testid="servizio-avanti"
              >
                Avanti: {successiva.label}
                <ArrowRight className="w-4 h-4" strokeWidth={2} />
              </button>
            )}
            <span className="flex-1 min-w-0 truncate text-[12.5px] font-medium text-[#c0392b]" data-testid="servizio-da-completare">
              {tentato && errori.sezioni.size > 0 &&
                `Da completare: ${ORDINE.filter((s) => errori.sezioni.has(s.id)).map((s) => s.label).join(", ")}`}
            </span>
            <button
              type="button"
              onClick={chiudi}
              className="h-10 px-[18px] rounded-[11px] border border-[#e0e0ea] bg-white text-[13.5px] font-semibold text-[#565d6d] hover:border-[#c9c9f0] transition-colors"
            >
              Annulla
            </button>
            <button
              type="button"
              onClick={salva}
              disabled={salvando}
              className="h-10 px-[22px] rounded-[11px] bg-[#5b5bd6] hover:bg-[#4a4ac4] disabled:bg-[#c7c7e4] text-[13.5px] font-semibold text-white flex items-center gap-2 transition-colors"
              data-testid="salva-servizio-btn"
            >
              {salvando && <Loader2 className="w-4 h-4 animate-spin" />}
              {salvando ? "Salvataggio..." : "Salva"}
            </button>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!conferma} onOpenChange={(v) => { if (!v) setConferma(null); }}>
        <AlertDialogContent className={DIALOG_CONTENT_CLS} data-testid="servizio-conferma-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-[#15161c]">{conferma?.titolo}</AlertDialogTitle>
            <AlertDialogDescription>{conferma?.descrizione}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => { conferma?.azione(); setConferma(null); }}
              className="h-9 px-5 rounded-[10px] bg-[#c0392b] hover:bg-[#a93226] text-white text-[13px] font-semibold transition-colors scheda-font-ui"
            >
              {conferma?.etichetta}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
